import { z } from 'zod'
import { asEnumType, asObjectType } from '@gqloom/zod'
import { clientFiltersSchema } from './client-filter-schemas'

// Filter type constants
export const StaffUserSmartGroupFilterType = {
  Schedule: 'schedule',
  Contract: 'contract',
  Profile: 'profile',
  Attendance: 'attendance',
} as const

export const staffUserSmartGroupFilterTypeEnumSchema = z
  .enum(StaffUserSmartGroupFilterType)
  .register(asEnumType, {
    name: 'StaffUserSmartGroupFilterType',
  })

// Logical operator constants
export const StaffUserSmartGroupLogicalOperator = {
  And: 'and',
  Or: 'or',
} as const

export const staffUserSmartGroupLogicalOperatorEnumSchema = z
  .enum(StaffUserSmartGroupLogicalOperator)
  .register(asEnumType, {
    name: 'StaffUserSmartGroupLogicalOperator',
  })

// Create a copy of clientFiltersSchema without the GraphQL registration to avoid duplicate types
const smartGroupFiltersSchema = z.object(clientFiltersSchema.shape).optional()

// Staff user smart group schema (matches the database table structure)
export const staffUserSmartGroupSchema = z.object({
  staffUserSmartGroupId: z.uuid(),
  staffUserId: z.string(),
  name: z.string().min(1, 'Name is required').max(255, 'Name must be 255 characters or less'),
  isVisible: z.boolean().default(true),
  sortOrder: z.number().int(),
  filters: smartGroupFiltersSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullish(),
})

export type StaffUserSmartGroupSchema = z.infer<typeof staffUserSmartGroupSchema>

// Public schema for GraphQL (excludes internal fields)
export const staffUserSmartGroupPublicSchema = staffUserSmartGroupSchema
  .omit({
    staffUserSmartGroupId: true,
    createdAt: true,
    updatedAt: true,
    deletedAt: true,
  })
  .extend({
    id: z.string(), // Maps to staffUserSmartGroupId for frontend compatibility
  })
  .register(asObjectType, {
    name: 'StaffUserSmartGroup',
    description: 'Staff user smart group with filters',
  })

export type StaffUserSmartGroupPublicSchema = z.infer<typeof staffUserSmartGroupPublicSchema>

// Create schema for new smart groups
export const staffUserSmartGroupCreateSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255, 'Name must be 255 characters or less'),
  isVisible: z.boolean().default(true),
  filters: smartGroupFiltersSchema,
})

export type StaffUserSmartGroupCreateSchema = z.infer<typeof staffUserSmartGroupCreateSchema>

// Update schema for existing smart groups
export const staffUserSmartGroupUpdateSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .max(255, 'Name must be 255 characters or less')
    .optional(),
  isVisible: z.boolean().optional(),
  filters: smartGroupFiltersSchema,
})

export type StaffUserSmartGroupUpdateSchema = z.infer<typeof staffUserSmartGroupUpdateSchema>

// Dialog form schema for creating/editing smart groups in the frontend
export const staffUserSmartGroupDialogFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255, 'Name must be 255 characters or less'),
  filters: z
    .array(
      z.object({
        type: z.string(),
        operator: z.string(),
        value: z.any(),
        logicalOperator: z.enum(['and', 'or']).default('and'),
        orderIndex: z.number().int().min(0),
      })
    )
    .default([]),
})

export type StaffUserSmartGroupDialogFormSchema = z.infer<
  typeof staffUserSmartGroupDialogFormSchema
>
